import { useLazyQuery } from "@apollo/client/react";
import { useRef, useState } from "react";
import { obtenerCoordenadas } from "../constants/getUbicacion";
import { VALIDAR_DISTANCIA_VISITA } from "../../../data/graphQL/queries/MisVisitas/validaciones/validarDistanciaVisita";
import { MensajeAdvertenciaDialog } from "../components/components/Dialogs/MensajeAdvertenciaDialog";

/**
 * Hook personalizado para validar la distancia de una visita
 * Maneja la lógica de validación, modal y promesas pendientes
 * 
 * @returns {Object} Objeto con:
 *   - validarDistancia: función async que valida la distancia
 *   - modal: componente JSX del modal de advertencia
 * 
 * @example
 * const { validarDistancia, modal } = useValidacionDistanciaVisita();
 * 
 * const handleFinalizar = async () => {
 *   const resultado = await validarDistancia(idVisita);
 *   if (resultado.permitir) {
 *     // Continuar con el flujo
 *   }
 * };
 */
export function useValidacionDistanciaVisita() {
    const [modalMensajeDistanciaVisita, setModalMensajeDistanciaVisita] = useState(false);
    const [errorDistancia, setErrorDistancia] = useState(null);
    const pendingRef = useRef({});

    const [validarDistanciaVisita] = useLazyQuery(VALIDAR_DISTANCIA_VISITA, {
        fetchPolicy: 'no-cache'
    });

    /**
     * Valida la distancia de la visita
     * Si hay error, muestra el modal y retorna una promesa que se resuelve 
     * cuando el usuario hace clic en aceptar o cancelar
     * 
     * @param {number} idVisita - ID de la visita a validar
     * @returns {Promise<Object>} Promesa que resuelve con { permitir: boolean }
     */
    const validarDistancia = async (idVisita, { omitirValidacion = false } = {}) => {
        if (omitirValidacion) {
            return { permitir: true };
        }

        try {
            const coordenadas = await obtenerCoordenadas();

            const { data: dataValidarDistancia } = await validarDistanciaVisita({
                variables: {
                    idVisita,
                    latitud: coordenadas?.latitud?.toString(),
                    longitud: coordenadas?.longitud?.toString()
                }
            });

            // Si hay error de distancia, mostrar modal
            if (!!dataValidarDistancia?.validarDistanciaVisita?.error) {
                setErrorDistancia(dataValidarDistancia.validarDistanciaVisita.error);
                setModalMensajeDistanciaVisita(true);

                // Retornar una promesa que se resuelve cuando el usuario interactúa con el modal
                return new Promise((resolve) => {
                    pendingRef.current = { resolve };
                });
            }

            // No hay error, permitir continuar
            return { permitir: true };
        } catch (error) {
            console.error("Error al validar distancia:", error);
            throw error;
        }
    };

    /**
     * Handler para cuando el usuario acepta la validación
     */
    const handleAccept = () => {
        setModalMensajeDistanciaVisita(false);
        const pending = pendingRef.current;

        if (pending?.resolve) {
            pendingRef.current = {};
            pending.resolve({ permitir: true });
        }
    };

    /**
     * Handler para cuando el usuario cancela la validación
     */
    const handleCancel = () => {
        setModalMensajeDistanciaVisita(false);
        const pending = pendingRef.current;

        if (pending?.resolve) {
            pendingRef.current = {};
            pending.resolve({ permitir: false });
        }
    };

    /**
     * Componente del modal - renderizar en el JSX del componente
     */
    const modal = (
        <MensajeAdvertenciaDialog
            open={modalMensajeDistanciaVisita}
            onClose={handleCancel}
            onAccept={handleAccept}
            onCancel={handleCancel}
            message={errorDistancia}
            acceptText="Finalizar"
            cancelText="Cancelar"
            mustGoBack={false}
        />
    );

    return {
        validarDistancia,
        modal
    };
}